import React, { useEffect, useState, useRef } from 'react';
import {
  Button,
  Col,
  Input,
  Row,
  Form,
  Typography,
  Spin,
  ConfigProvider,
} from 'antd';
import ErrorAlert from '../error_alert';
import { useAtom } from 'jotai';
import {
  patientVerificationDataAtom,
  verificationTokenAtom,
  careProviderConfiguration,
} from '../../store';
import { useNavigate, useParams } from 'react-router-dom';
import Header from '../header';
import {
  LoginFormType,
  LoginResponseType,
} from '../../models/verification.interface';
import { usePostQuery } from '../../api/hooks/usePostQuery';
import type { InputRef } from 'antd';
import DateOfBirth from 'components/date_of_birth';
import './verification.css';

const Verification: React.FC<{
  configLoading: boolean;
  configIdling: boolean;
  displayText: {
    openingMessage?: string;
    welcomeMessage?: string;
  };
}> = ({ configLoading, configIdling, displayText }) => {
  const { Paragraph, Title } = Typography;
  const [form] = Form.useForm();
  const { careProviderSlug = '' } = useParams();
  const navigate = useNavigate();
  const [, setVerificationToken] = useAtom(verificationTokenAtom);
  const [, setPatientData] = useAtom(patientVerificationDataAtom);
  const [careProviderStyles] = useAtom(careProviderConfiguration);

  const [isPristine, setIsPristine] = useState<boolean>(true);
  const phoneRef = useRef<InputRef>(null);

  const { data, exec, isError, isPending, isSuccess } = usePostQuery<
    LoginFormType,
    LoginResponseType
  >('login');

  const onFinish = async (fieldsValue: LoginFormType) => {
    const values = {
      firstName: fieldsValue.firstName.trim(),
      lastName: fieldsValue.lastName.trim(),
      dateOfBirth: fieldsValue.dateOfBirth,
      careProviderSlug,
    };
    await exec(values);
    setIsPristine(true);
  };

  const onValuesChange = (currentVals: any, allVals: any) => {
    if (currentVals.firstName === 'triggerBugsnagError') {
      // controlled error
      const triggerBugsnag: any = {};
      console.log(triggerBugsnag.error.now());
    }
    // eslint-disable-next-line @typescript-eslint/no-unused-expressions
    isError && setIsPristine(false);
    form.setFieldsValue({
      dateOfBirth: `${allVals.month}/${allVals.day}/${allVals.year}`,
    });
    if (currentVals?.year?.length === 4 && phoneRef.current) {
      phoneRef.current.focus();
    }
  };

  useEffect(() => {
    if (isSuccess && data?.token && data?.patient) {
      setVerificationToken(data.token);
      setPatientData(data.patient);
      navigate('./landing-page', { relative: 'path' });
    }
  });

  return (
    <>
      <Header background={careProviderStyles?.colorBgHeader || 'transparent'} />
      <Row style={{ height: '100%', padding: '50px 32px' }}>
        <Col
          xs={{ offset: 0, span: 24 }}
          md={{ offset: 4, span: 16 }}
          lg={{ offset: 6, span: 12 }}
        >
          {configIdling || configLoading ? <Spin /> : null}
          <Row justify="space-between" align="middle">
            <Title level={2} data-cy="opening_message">
              {displayText?.openingMessage || ''}
            </Title>
          </Row>
          <Col>
            <Paragraph style={{ margin: '40px 0 50px ' }}>
              {displayText?.welcomeMessage || ''}
            </Paragraph>
          </Col>
          <Row>
            <Col span={24}>
              <Title level={4}>First, let&apos;s verify your identity</Title>
              <Form
                className="verification-form"
                onValuesChange={onValuesChange}
                layout="vertical"
                form={form}
                onFinish={onFinish}
                name="verificationForm"
                data-cy="verification_form"
              >
                <Form.Item
                  label="First Name"
                  name="firstName"
                  rules={[
                    { required: true, message: 'Please input your first name' },
                  ]}
                >
                  <Input
                    placeholder="First Name"
                    name="firstName"
                    required
                    size="large"
                    data-cy="first_name"
                  />
                </Form.Item>
                <Form.Item
                  label="Last Name"
                  name="lastName"
                  rules={[
                    { required: true, message: 'Please input your last name' },
                  ]}
                >
                  <Input
                    placeholder="Last Name"
                    name="lastName"
                    required
                    size="large"
                    data-cy="last_name"
                  />
                </Form.Item>
                <DateOfBirth form={form} />
                {isError && isPristine && (
                  <Form.Item>
                    <ErrorAlert message="We couldn't validate your identity. Confirm that the name and date of birth you entered match the information you provided during check-in." />
                  </Form.Item>
                )}
                <Form.Item shouldUpdate>
                  {() => (
                    <ConfigProvider
                      theme={{
                        token: {
                          colorPrimary: careProviderStyles.colorPrimary,
                        },
                      }}
                    >
                      <Button
                        type="primary"
                        htmlType="submit"
                        shape="round"
                        data-cy="submit"
                        loading={isPending}
                        block
                        style={{ height: '40px', marginTop: '10px' }}
                        disabled={
                          !form.isFieldsTouched(true)
                          || !!form
                            .getFieldsError()
                            .filter(({ errors }) => errors.length).length
                        }
                      >
                        Submit
                      </Button>
                    </ConfigProvider>
                  )}
                </Form.Item>
              </Form>
            </Col>
          </Row>
        </Col>
      </Row>
    </>
  );
};

export default Verification;
